import React, { useEffect, useRef, useState } from 'react';
import { Card, Button, Badge, EmptyState, ErrorNote } from '../ui';

interface ContentRow {
  id: string;
  pageId: string;
  title: string | null;
  topic: string | null;
  state: string;
  qualityScore: number | null;
  revision: number;
  updatedAt: string;
  page: { internalLabel: string; timeZone: string };
  imagePost: { caption: string | null; concept: string | null; imageAssetId: string | null } | null;
  sources: { id: string; sourceTitle: string; publisher: string | null; url: string; publishedAt: string | null }[];
  qualityScores: { overall: number; explanation: string | null; blockingIssues: string[]; warnings: string[] }[];
  schedules: { publishAt: string; method: string; status: string }[];
  publications: { fbObjectId: string; publishedAt: string }[];
}

interface PageOption { id: string; internalLabel: string }
interface ImageReferenceRow { id: string; mimeType: string; width: number | null; height: number | null }

const STATE_BADGE: Record<string, string> = {
  PUBLISHED: 'HEALTHY', SCHEDULED: 'HEALTHY', APPROVED: 'HEALTHY',
  REVIEW_REQUIRED: 'RATE_LIMITED', RETRY_WAIT: 'RATE_LIMITED',
  BLOCKED: 'MISCONFIGURED', FAILED: 'MISCONFIGURED', CANCELLED: 'ARCHIVED',
};

const GROUPS: { label: string; states: string[] }[] = [
  { label: 'Generating', states: ['IDEA', 'RESEARCHING', 'RESEARCH_COMPLETE', 'GENERATING_TITLE', 'TITLE_READY', 'GENERATING_MEDIA', 'MEDIA_READY', 'GENERATING_CAPTION', 'CAPTION_READY', 'VALIDATING'] },
  { label: 'Awaiting approval', states: ['REVIEW_REQUIRED'] },
  { label: 'Approved & scheduled', states: ['APPROVED', 'SCHEDULED', 'PUBLISHING', 'FACEBOOK_PROCESSING'] },
  { label: 'Published', states: ['PUBLISHED'] },
  { label: 'Problems', states: ['RETRY_WAIT', 'BLOCKED', 'FAILED'] },
];

export function ImagePostsScreen({ pageId }: { pageId?: string } = {}) {
  const [items, setItems] = useState<ContentRow[]>([]);
  const [pages, setPages] = useState<PageOption[]>([]);
  const [localFilter, setLocalFilter] = useState('');
  // When rendered inside a Page workspace the filter is fixed to that Page and
  // the picker is hidden; standalone it falls back to the local dropdown.
  const pageFilter = pageId ?? localFilter;
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [images, setImages] = useState<Record<string, string>>({});
  const [fullImage, setFullImage] = useState<{ url: string; title: string } | null>(null);
  const [references, setReferences] = useState<ImageReferenceRow[]>([]);
  const [referencePreviews, setReferencePreviews] = useState<Record<string, string>>({});
  const [uploadingReference, setUploadingReference] = useState(false);
  const [startingGeneration, setStartingGeneration] = useState(false);
  const referenceInput = useRef<HTMLInputElement>(null);

  const load = async () => {
    try {
      setPages((await window.pagepilot.pages.list()) as PageOption[]);
      const rows = (await window.pagepilot.content.list(pageFilter || undefined, 'IMAGE_POST')) as ContentRow[];
      setItems(rows);
      setError(null);
      for (const r of rows) {
        if (r.imagePost?.imageAssetId && !images[r.id]) {
          const url = (await window.pagepilot.content.imageDataUrl(r.id)) as string | null;
          if (url) setImages((prev) => ({ ...prev, [r.id]: url }));
        }
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  };
  useEffect(() => {
    void load();
    const t = setInterval(() => void load(), 12_000);
    return () => clearInterval(t);
  }, [pageFilter]);

  const loadReferences = async (pageId: string) => {
    if (!pageId) {
      setReferences([]);
      setReferencePreviews({});
      return;
    }
    const rows = (await window.pagepilot.pages.listImageReferences(pageId)) as ImageReferenceRow[];
    setReferences(rows);
    const previews = await Promise.all(rows.map(async (reference) => [reference.id, await window.pagepilot.assets.dataUrl(reference.id)] as const));
    setReferencePreviews(Object.fromEntries(previews.filter(([, url]) => Boolean(url))) as Record<string, string>);
  };

  useEffect(() => {
    void loadReferences(pageFilter).catch((e) => setError(e instanceof Error ? e.message : String(e)));
  }, [pageFilter]);

  const uploadReference = (file: File) => {
    if (!pageFilter) return;
    const reader = new FileReader();
    reader.onload = async () => {
      setUploadingReference(true);
      setError(null);
      try {
        await window.pagepilot.pages.uploadImageReference({ pageId: pageFilter, dataUrl: reader.result as string });
        await loadReferences(pageFilter);
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploadingReference(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <h1 className="text-lg font-semibold">Image Posts</h1>
        {!pageId && (
          <select
            value={localFilter}
            onChange={(e) => setLocalFilter(e.target.value)}
            className="rounded-lg border border-neutral-700 bg-neutral-900 px-2 py-1.5 text-sm"
          >
            <option value="">Step 1: choose a Page</option>
            {pages.map((p) => (
              <option key={p.id} value={p.id}>{p.internalLabel}</option>
            ))}
          </select>
        )}
      </div>
      {error && <ErrorNote message={error} />}
      {!pageFilter && (
        <Card title="Create an image post">
          <p className="text-sm text-neutral-400">Start by choosing a Page above. Then attach the examples that define the visual style you want for its image posts.</p>
        </Card>
      )}
      {pageFilter && (
        <Card title="Step 2: attach visual references">
          <p className="mb-3 text-xs text-neutral-400">Attach examples of the look you want. Every new image post uses the displayed images as style, palette, composition and layout templates. The first image also supplies the top-right Page brand mark that is added to every finished post.</p>
          <input
            ref={referenceInput}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.currentTarget.value = '';
              if (file) uploadReference(file);
            }}
          />
          <div className="flex flex-wrap items-start gap-3">
            {references.map((reference, index) => (
              <div key={reference.id} className="w-28">
                <div className="h-36 overflow-hidden rounded-lg border border-neutral-700 bg-neutral-800">
                  {referencePreviews[reference.id]
                    ? <img src={referencePreviews[reference.id]} alt={`Visual reference ${index + 1}`} className="h-full w-full object-cover" />
                    : <div className="flex h-full items-center justify-center text-[10px] text-neutral-500">Preview unavailable</div>}
                </div>
                <p className="mt-1 text-[10px] text-neutral-500">Reference {index + 1}{reference.width && reference.height ? ` - ${reference.width} x ${reference.height}` : ''}</p>
                <button
                  className="text-xs text-red-300 hover:underline"
                  onClick={async () => {
                    try {
                      await window.pagepilot.pages.removeImageReference(pageFilter, reference.id);
                      await loadReferences(pageFilter);
                    } catch (e) {
                      setError(e instanceof Error ? e.message : String(e));
                    }
                  }}
                >
                  Remove
                </button>
              </div>
            ))}
            {references.length < 5 && (
              <button
                disabled={uploadingReference}
                onClick={() => referenceInput.current?.click()}
                className="flex h-36 w-28 flex-col items-center justify-center rounded-lg border border-dashed border-brand-500/60 bg-brand-500/5 text-xs text-brand-300 hover:bg-brand-500/10 disabled:opacity-50"
              >
                {uploadingReference ? 'Uploading...' : '+ Attach image'}
              </button>
            )}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-3">
            <div>
              <p className="text-sm font-medium text-neutral-200">Step 3: generate the post</p>
              <p className="text-xs text-neutral-500">Output: 1080 x 1350 px - portrait 4:5. {references.length}/5 visual references attached.</p>
            </div>
            <div className="flex gap-2">
              <Button
                disabled={references.length === 0 || uploadingReference || startingGeneration}
                title={references.length === 0 ? 'Attach at least one visual reference image first' : ''}
                onClick={async () => {
                  setStartingGeneration(true);
                  setError(null);
                  try {
                    await window.pagepilot.content.generateNow(pageFilter, 'IMAGE_POST');
                    await load();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setStartingGeneration(false);
                  }
                }}
              >
                {startingGeneration ? 'Starting...' : 'Generate 1 image'}
              </Button>
              <Button
                variant="ghost"
                disabled={references.length === 0 || uploadingReference || startingGeneration}
                title={references.length === 0 ? 'Attach at least one visual reference image first' : 'Fill the rest of the month at your per-day rate'}
                onClick={async () => {
                  if (!window.confirm('Generate a full month of image posts? They run through the pipeline and auto-schedule across the 3-hour grid (and publish live if approval is automatic).')) return;
                  setStartingGeneration(true);
                  setError(null);
                  try {
                    await window.pagepilot.content.generateMonth(pageFilter, 'IMAGE_POST', 30);
                    await load();
                  } catch (e) {
                    setError(e instanceof Error ? e.message : String(e));
                  } finally {
                    setStartingGeneration(false);
                  }
                }}
              >
                Generate the month
              </Button>
            </div>
          </div>
        </Card>
      )}
      {!pageFilter && items.length === 0 && (
        <EmptyState
          title="No image posts yet"
          hint="Select a Page and press Generate now, or wait for the daily generation time. The pipeline researches real sources, generates the title, image and caption, reviews quality, then schedules."
        />
      )}

      {GROUPS.map((g) => {
        const rows = items.filter((i) => g.states.includes(i.state));
        if (rows.length === 0) return null;
        return (
          <div key={g.label}>
            <h2 className="mb-2 text-sm font-semibold text-neutral-400">{g.label} ({rows.length})</h2>
            <div className="grid grid-cols-2 gap-4">
              {rows.map((item) => (
                <Card key={item.id}>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      disabled={!images[item.id]}
                      title={images[item.id] ? 'View full image' : 'Image is not ready yet'}
                      onClick={() => {
                        const url = images[item.id];
                        if (url) setFullImage({ url, title: item.title ?? item.topic ?? 'Generated image' });
                      }}
                      className="h-24 w-24 shrink-0 overflow-hidden rounded-lg bg-neutral-800 text-left disabled:cursor-default"
                    >
                      {images[item.id] ? (
                        <img src={images[item.id]} alt={`View full image: ${item.title ?? item.topic ?? 'generated image'}`} className="h-full w-full object-cover transition-opacity hover:opacity-80" />
                      ) : (
                        <div className="flex h-full items-center justify-center text-[10px] text-neutral-600">no image yet</div>
                      )}
                    </button>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <p className="truncate text-sm font-semibold">{item.title ?? item.topic ?? 'Generating…'}</p>
                        <Badge status={STATE_BADGE[item.state] ?? 'BUSY'} label={item.state.replaceAll('_', ' ').toLowerCase()} />
                      </div>
                      <p className="text-xs text-neutral-500">{item.page.internalLabel} · rev {item.revision}{item.qualityScore !== null ? ` · quality ${item.qualityScore}/100` : ''}</p>
                      {item.schedules[0] && (
                        <p className="text-xs text-neutral-500">
                          {item.schedules[0].status === 'DONE' ? 'Published' : 'Scheduled'} {new Date(item.schedules[0].publishAt).toLocaleString()} · {item.schedules[0].method === 'FACEBOOK_NATIVE' ? 'Facebook native' : 'PagePilot scheduler'}
                        </p>
                      )}
                      {item.imagePost?.caption && (
                        <p className="mt-1 line-clamp-2 text-xs text-neutral-400">{item.imagePost.caption}</p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3 flex flex-wrap gap-2">
                    {item.state === 'REVIEW_REQUIRED' && (
                      <>
                        <Button onClick={async () => { await window.pagepilot.content.approve(item.id); void load(); }}>Approve & schedule</Button>
                        <Button variant="danger" onClick={async () => { await window.pagepilot.content.reject(item.id, 'Rejected in review'); void load(); }}>Reject</Button>
                      </>
                    )}
                    {item.state !== 'PUBLISHED' && item.publications.length === 0 && (
                      <Button
                        variant="danger"
                        onClick={async () => {
                          if (!window.confirm('Remove this image post? Its queued generation and schedule will be cancelled. This cannot be undone.')) return;
                          try {
                            setError(null);
                            await window.pagepilot.content.remove(item.id);
                            setExpanded((current) => current === item.id ? null : current);
                            setImages((current) => {
                              const next = { ...current };
                              delete next[item.id];
                              return next;
                            });
                            await load();
                          } catch (e) {
                            setError(e instanceof Error ? e.message : String(e));
                          }
                        }}
                      >
                        Remove post
                      </Button>
                    )}
                    <Button variant="ghost" onClick={() => setExpanded(expanded === item.id ? null : item.id)}>
                      {expanded === item.id ? 'Hide details' : 'Details & sources'}
                    </Button>
                    {images[item.id] && (
                      <Button variant="ghost" onClick={() => {
                        const url = images[item.id];
                        if (url) setFullImage({ url, title: item.title ?? item.topic ?? 'Generated image' });
                      }}>
                        View full image
                      </Button>
                    )}
                  </div>

                  {expanded === item.id && (
                    <div className="mt-3 space-y-3 rounded-lg border border-neutral-800 bg-neutral-950 p-3 text-xs">
                      {item.imagePost?.concept && (
                        <div>
                          <p className="mb-1 font-semibold text-neutral-400">Concept</p>
                          <p className="text-neutral-300">{item.imagePost.concept}</p>
                        </div>
                      )}
                      {item.imagePost?.caption && (
                        <div>
                          <p className="mb-1 font-semibold text-neutral-400">Caption</p>
                          <p className="whitespace-pre-wrap text-neutral-300">{item.imagePost.caption}</p>
                        </div>
                      )}
                      {item.qualityScores[0] && (
                        <div>
                          <p className="mb-1 font-semibold text-neutral-400">Review ({item.qualityScores[0].overall}/100)</p>
                          <p className="text-neutral-300">{item.qualityScores[0].explanation}</p>
                          {item.qualityScores[0].blockingIssues.length > 0 && (
                            <p className="mt-1 text-red-400">Blocking: {item.qualityScores[0].blockingIssues.join('; ')}</p>
                          )}
                          {item.qualityScores[0].warnings.length > 0 && (
                            <p className="mt-1 text-amber-400">Warnings: {item.qualityScores[0].warnings.join('; ')}</p>
                          )}
                        </div>
                      )}
                      <div>
                        <p className="mb-1 font-semibold text-neutral-400">Sources ({item.sources.length})</p>
                        {item.sources.length === 0 ? (
                          <p className="text-neutral-500">No sources recorded.</p>
                        ) : (
                          <ul className="space-y-1">
                            {item.sources.slice(0, 8).map((s) => (
                              <li key={s.id} className="text-neutral-400">
                                <span className="tabular-nums text-neutral-600">{s.publishedAt ? new Date(s.publishedAt).toISOString().slice(0, 10) : 'undated'}</span>{' '}
                                {s.sourceTitle} <span className="text-neutral-600">({s.publisher ?? 'unknown'})</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      {item.publications[0] && (
                        <p className="text-emerald-400">Facebook ID: {item.publications[0].fbObjectId}</p>
                      )}
                    </div>
                  )}
                </Card>
              ))}
            </div>
          </div>
        );
      })}
      {fullImage && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-6"
          role="dialog"
          aria-modal="true"
          aria-label="Full generated image"
          onClick={() => setFullImage(null)}
        >
          <div className="max-h-full max-w-full" onClick={(event) => event.stopPropagation()}>
            <div className="mb-2 flex items-center justify-between gap-4">
              <p className="truncate text-sm font-medium text-white">{fullImage.title}</p>
              <Button variant="ghost" onClick={() => setFullImage(null)}>Close</Button>
            </div>
            <img
              src={fullImage.url}
              alt={fullImage.title}
              className="max-h-[calc(100vh-7rem)] max-w-[calc(100vw-3rem)] rounded-lg object-contain shadow-2xl"
            />
          </div>
        </div>
      )}
    </div>
  );
}
